import React, { useContext, useState } from 'react';
import Dropzone from 'react-dropzone';
import { Tooltip } from '@mui/material';
import { format } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faDownload, faExclamationCircle, faFileExcel, faTimes } from '@fortawesome/free-solid-svg-icons';
import { Modal } from '@stormwater-software/lib-ui';
import AppDivisionContext from '../../../contexts/AppDivisionContext';
import { BulkUploadApi, BulkUploadResult } from '../../../utils/api/bulkUpload';

const TOOLTIP = 'Tenant Features change often, so it is recommended to use latest template';

interface IBulkUploadModalProps {
  entity: string;
  api: BulkUploadApi | null;
  handleClose: () => void;
  onCreated: () => void;
  templateNote?: React.ReactNode;
  fileLabel?: string;
  tooltip?: string;
}

// Shared by Add Bulk Client and Add Bulk Project; `entity` is the singular noun shown in the copy.
const BulkUploadModal: React.FC<IBulkUploadModalProps> = ({
  entity,
  api,
  handleClose,
  onCreated,
  templateNote,
  fileLabel,
  tooltip = TOOLTIP,
}) => {
  const { appDivisionId, divisions } = useContext(AppDivisionContext);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [results, setResults] = useState<BulkUploadResult[] | null>(null);
  const divisionName = divisions.find(d => d.id === appDivisionId)?.name || '';

  const run = async (action: () => Promise<void>, failure: string) => {
    setBusy(true);
    setErrorMsg('');
    try {
      await action();
    } catch (error) {
      setErrorMsg((error as any)?.response?.data?.message || failure);
    } finally {
      setBusy(false);
    }
  };

  const download = () =>
    run(async () => {
      const link = document.createElement('a');

      link.href = URL.createObjectURL(await api!.downloadTemplate());
      link.download = `Bulk ${entity} Upload - ${fileLabel || divisionName} - ${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
      link.click();
      URL.revokeObjectURL(link.href);
    }, 'The template could not be downloaded.');

  const upload = () =>
    run(async () => {
      const response = await api!.upload(file!);

      setResults(response.results);
      if (response.created || response.updated) onCreated();
    }, 'The upload failed. Please try again.');

  const count = (status: BulkUploadResult['status']) => results?.filter(r => r.status === status).length || 0;
  const created = count('created');
  const updated = count('updated');

  return (
    <Modal
      show
      title={`Add Bulk ${entity}`}
      classes='bulk-upload'
      handleClose={handleClose}
      handleSubmit={results ? handleClose : upload}
      submitBtnText={results ? 'Done' : busy ? 'Uploading...' : 'Upload'}
      disableSubmit={!results && (!file || !api || busy)}
      errorMsg={errorMsg}
    >
      {results ? (
        <>
          <div className='bulk-upload__summary'>
            <span className='is-created'><FontAwesomeIcon icon={faCheckCircle} /> {created} created</span>
            {updated > 0 && (
              <span className='is-created'><FontAwesomeIcon icon={faCheckCircle} /> {updated} updated</span>
            )}
            {results.length > created + updated && (
              <span className='is-failed'><FontAwesomeIcon icon={faExclamationCircle} /> {results.length - created - updated} failed</span>
            )}
          </div>
          <div className='bulk-upload__results'>
            <table>
              <thead>
                <tr><th>Row</th><th>{entity}</th><th>Result</th></tr>
              </thead>
              <tbody>
                {results.map(r => (
                  <tr key={r.row}>
                    <td>{r.row}</td>
                    <td>{r.name || '-'}</td>
                    <td className={`is-${r.status === 'updated' ? 'created' : r.status}`}>
                      {r.status === 'created' ? 'Created' : r.status === 'updated' ? 'Updated' : r.error}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <>
          <div className='bulk-upload__step'>
            <span className='bulk-upload__number'>1</span>
            <div>
              <h4 style={{padding: 0}}>Download the template</h4>
              {templateNote || <p>Built for <strong>{divisionName}</strong> with the current tenant features.</p>}
              <Tooltip title={tooltip} arrow describeChild enterTouchDelay={0}>
                <button type='button' className='outline' onClick={download} disabled={busy || !api}>
                  <FontAwesomeIcon icon={faDownload} /> Download latest template
                </button>
              </Tooltip>
            </div>
          </div>
          <div className='bulk-upload__step'>
            <span className='bulk-upload__number'>2</span>
            <div>
              <h4 style={{padding: 0}}>Upload the filled-in template</h4>
              <p>One {entity.toLowerCase()} per row. Columns marked * are required.</p>
              {file ? (
                <div className='bulk-upload__file'>
                  <FontAwesomeIcon icon={faFileExcel} />
                  <span>{file.name}</span>
                  <button type='button' aria-label='Remove file' onClick={() => setFile(null)}>
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
              ) : (
                <Dropzone
                  accept='.xlsx'
                  multiple={false}
                  maxSize={5 * 1024 * 1024}
                  onDropAccepted={([accepted]) => setFile(accepted)}
                  onDropRejected={() => setErrorMsg('Upload an .xlsx file under 5 MB.')}
                >
                  {({ getRootProps, getInputProps, isDragActive }) => (
                    <div {...getRootProps({ className: `bulk-upload__dropzone${isDragActive ? ' is-active' : ''}` })}>
                      <input {...getInputProps()} aria-label='Template file' />
                      <FontAwesomeIcon icon={faFileExcel} size='2x' />
                      <span>Drag the .xlsx file here or <u>browse</u></span>
                    </div>
                  )}
                </Dropzone>
              )}
            </div>
          </div>
        </>
      )}
    </Modal>
  );
};

export default BulkUploadModal;
